import React, { TextareaHTMLAttributes } from "react";
import { FormIcon } from "@core/components/forms/FormIcon";
import { Textarea } from "@core/components/ui/textarea";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { cn } from "@core/lib/utils";
import { FieldErrors } from "react-hook-form";
import { ErrorMessage } from "@core/components/forms/ErrorMessage";

export type LabeledTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: React.ReactNode;
  icon?: IconDefinition;
  iconClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  errors?: FieldErrors<any>;
};

/**
 * Labeled Textareas are textareas with a `<label>` and support for an icon.
 * @example
 * ```tsx
 * <LabeledTextarea name="description" label="Description" icon={faInfoCircle} />
 * ```
 * They also support children, which are either displayed below the textarea or can be absolute:
 * ```tsx
 * <LabeledTextarea {...}>
 *   <div className="absolute p-2 right-0">
 *     <FontAwesomeIcon icon={faEye} />
 *   </div>
 * </LabeledTextarea>
 * ```
 */
export const LabeledTextarea = React.forwardRef<
  HTMLTextAreaElement,
  LabeledTextareaProps
>(({ label, children, className, labelClassName, icon, iconClassName, inputClassName, errors, id, name, ...props }, ref) => {
  const generatedId = React.useId();
  const inputId = id ?? name ?? generatedId;
  const roundedClass = props.icon ? "rounded-e-[var(--form-radius)] rounded-s-none" : "rounded-[var(--form-radius)]";
  return (
    <div className={className}>
      {label && (
        <label className={cn(labelClassName, "inline-block mb-1 text-sm")} htmlFor={inputId}>
          {label}{props.required && <span className="text-danger-500">&nbsp;*</span>}
        </label>
      )}
      <div className="flex relative">
        {icon && <FormIcon icon={icon} className={iconClassName} />}
        <Textarea className={cn(roundedClass, inputClassName)} id={inputId} name={name} {...props} ref={ref} />
        {children}
      </div>
      {name != null && errors != null && (
        <ErrorMessage errors={errors} name={name} />
      )}
    </div>
  );
});
LabeledTextarea.displayName = "LabeledTextarea";